import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Calendar, Clock, Search, Tag, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchNewsArticles, type NewsArticle } from "@/services/content";
import { STALE_TIMES, queryKeys } from "@/lib/query-config";
import { SEO } from "@/components/SEO";
import { siteConfig } from "@/lib/site-config";

const News = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = ["All", "Community Events", "Community Impact", "Educational Programs", "Celebrations", "Announcements"]; 

  // Fallback data when Supabase isn't configured or has no rows
  const staticNewsArticles: NewsArticle[] = [
    {
      id: 1,
      title: "Annual Community Festival Returns with Record Attendance",
      excerpt: "Our community came together to celebrate diversity with traditional food, music, and art from around the world.",
      date: "2024-09-15",
      category: "Community Events",
      author: "Maria Rodriguez",
      image: "/placeholder.svg",
      featured: true
    },
    {
      id: 2,
      title: "New Educational Library Opens to Support Community Research",
      excerpt: "We proudly announce the opening of our expanded educational library featuring local history archives and genealogy resources.",
      date: "2024-09-10",
      category: "Announcements",
      author: "James Washington",
      image: "/placeholder.svg",
      featured: false
    },
    {
      id: 3,
      title: "Student Performing Arts Cohort Showcases Talent",
      excerpt: "Young artists from our community performed original works celebrating local history and traditions.",
      date: "2024-09-08",
      category: "Educational Programs",
      author: "Sarah Johnson",
      image: "/placeholder.svg",
      featured: false
    },
    {
      id: 4,
      title: "Women's Wellness Program Expands Services",
      excerpt: "New workshops on financial literacy and health education now available to support women in our community.",
      date: "2024-09-05",
      category: "Community Impact",
      author: "Dr. Angela Davis",
      image: "/placeholder.svg",
      featured: false
    },
    {
      id: 5,
      title: "MLK Day Celebration Planning Begins",
      excerpt: "Community members invited to participate in planning committee for our annual Martin Luther King Jr. celebration.",
      date: "2024-09-01",
      category: "Celebrations",
      author: "Rev. Michael Brown",
      image: "/placeholder.svg",
      featured: false
    }
  ];

  const { data: newsData, isLoading } = useQuery({
    queryKey: queryKeys.news.list(),
    queryFn: async () => {
      const result = await fetchNewsArticles();
      return result.success ? result.data : [];
    },
    staleTime: STALE_TIMES.news,
  });

  const articles: NewsArticle[] = (newsData && newsData.length > 0) ? newsData : staticNewsArticles;

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredArticle = articles.find(article => article.featured);
  const regularArticles = filteredArticles.filter(article => !article.featured);

  return (
    <div className="min-h-screen">
      <SEO
        title="News & Articles"
        description="Stay updated with the latest community news, announcements, educational programs, and community impact stories."
        keywords={['news', 'articles', 'community', 'updates', 'announcements']}
        type="website"
      />
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-heritage">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Community News & Updates
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            Stay informed about events, programs, and community achievements 
            that strengthen our diverse community.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search and Filter */}
        <div className="mb-12">
          <div className="flex flex-col lg:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <Input
                placeholder="Search news articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 py-3"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "heritage" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className="mb-2"
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Featured Article */}
        {featuredArticle && selectedCategory === "All" && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Featured Story</h2>
            <Card className="overflow-hidden border-heritage/20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="aspect-video lg:aspect-auto">
                  <img
                    src={featuredArticle.image || "/placeholder.svg"}
                    alt={featuredArticle.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-8">
                  <Badge variant="secondary" className="mb-4">
                    <Tag className="w-3 h-3 mr-1" />
                    {featuredArticle.category}
                  </Badge>
                  <h3 className="text-2xl font-bold mb-4 text-foreground">
                    {featuredArticle.title}
                  </h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    {featuredArticle.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(featuredArticle.date).toLocaleDateString()}
                      </div>
                      <span>By {featuredArticle.author}</span>
                    </div>
                    <Button variant="heritage">
                      Read More
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </div>
            </Card>
          </div>
        )}

        {/* Articles Grid */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Latest News</h2>
          {isLoading ? (
            <div className="text-center py-12">Loading articles...</div>
          ) : regularArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {regularArticles.map((article) => (
                <Card key={article.id} className="overflow-hidden group hover:shadow-heritage transition-all duration-300">
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={article.image || "/placeholder.svg"}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="p-6">
                    <Badge variant="outline" className="mb-3">
                      <Tag className="w-3 h-3 mr-1" />
                      {article.category}
                    </Badge>
                    <h3 className="text-lg font-bold mb-3 text-foreground group-hover:text-heritage transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(article.date).toLocaleDateString()}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-heritage hover:text-heritage/80">
                        Read More
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg mb-4">No articles found matching your search.</p>
              <p className="text-muted-foreground">
                Try adjusting your search terms or category filter.
              </p>
            </div>
          )}
        </div>

        {/* Newsletter Signup */}
        <section className="py-16 px-8 bg-secondary/30 rounded-xl">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-foreground mb-6">
              Never Miss Community News
            </h2>
            <p className="text-muted-foreground mb-8">
              Subscribe to our newsletter for the latest updates on events, programs, 
              and community achievements delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                placeholder="Enter your email"
                type="email"
                className="flex-1"
              />
              <Button variant="heritage">
                Subscribe
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default News;